import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Heart, Bell, ShoppingBag, User, ChevronDown, Crown, X, LogOut, CreditCard, Package, Plus, Minus, Trash2, ArrowRight, Check, Calendar, Star, Gift, Store, Building2, Clock, MapPin, Briefcase, CalendarDays, Truck } from 'lucide-react';
import { useApp, LOGOS } from '../context/AppContext';
import { PRODUCTS, OFFICES, CATEGORIES, getNotificationsByUserId, getUserFavoriteOffices } from '../data/database';
import { CategoryIcon, getCategoryColorClass, getCategoryBgClass } from './CategoryIcons';
import { LanguageSelector, useI18n } from '../i18n';

// Category groups configuration
const CATEGORY_GROUPS_CONFIG = {
  bellezza: { nameKey: 'bellezza', color: 'pink' },
  salute: { nameKey: 'salute', color: 'red' },
  sport: { nameKey: 'sport', color: 'orange' },
  food: { nameKey: 'food', color: 'amber' },
  travel: { nameKey: 'travel', color: 'sky' },
  transport: { nameKey: 'transport', color: 'yellow' },
  auto: { nameKey: 'auto', color: 'slate' },
  casa: { nameKey: 'casa', color: 'teal' },
  professionisti: { nameKey: 'professionisti', color: 'indigo' },
  formazione: { nameKey: 'formazione', color: 'purple' },
  eventi: { nameKey: 'eventi', color: 'rose' },
  shopping: { nameKey: 'shopping', color: 'emerald' },
  digitale: { nameKey: 'digitale', color: 'blue' },
};

const DesktopHeader = ({ activeTab, setActiveTab, onNavigate, currentPage }) => {
  const { user, isLoggedIn, cart, cartCount, requireAuth, logout, removeFromCart, updateCartQty, userMode, selectedOffice, currentCompany, companyOffices, switchOffice } = useApp();
  const { t, tc, tg } = useI18n();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [activeMenuGroup, setActiveMenuGroup] = useState(null);
  const [showOfficeSwitcher, setShowOfficeSwitcher] = useState(false);
  const officeSwitcherRef = useRef(null);
  
  // Check if user is in partner mode
  const isPartnerMode = userMode === 'partner' && isLoggedIn;
  
  // Helper per formattare il tempo
  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 60) return `${diffMins} minuti fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    if (diffDays === 1) return 'Ieri';
    return `${diffDays} giorni fa`;
  };

  // Notifiche e preferiti dal database
  const notifications = useMemo(() => {
    if (!user?.id) return [];
    return getNotificationsByUserId(user.id).map(n => ({
      ...n,
      time: formatTimeAgo(n.date),
    }));
  }, [user?.id]);
  
  const favorites = useMemo(() => {
    if (!user?.id) return [];
    return getUserFavoriteOffices(user.id);
  }, [user?.id]);
  
  // Dropdown states
  const [showCartDropdown, setShowCartDropdown] = useState(false);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [showFavoritesDropdown, setShowFavoritesDropdown] = useState(false);
  const [showConfigDropdown, setShowConfigDropdown] = useState(false);
  const configRef = useRef(null);

  const cartRef = useRef(null);
  const notificationsRef = useRef(null);
  const favoritesRef = useRef(null);
  const userMenuRef = useRef(null);
  const menuRef = useRef(null);
  const menuTimeoutRef = useRef(null);

  // Group categories by their group
  const groupedCategories = useMemo(() => {
    const groups = {};
    CATEGORIES.forEach(cat => {
      if (!groups[cat.group]) groups[cat.group] = [];
      groups[cat.group].push(cat);
    });
    return groups;
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cartRef.current && !cartRef.current.contains(e.target)) {
        setShowCartDropdown(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) {
        setShowNotificationsDropdown(false);
      }
      if (favoritesRef.current && !favoritesRef.current.contains(e.target)) {
        setShowFavoritesDropdown(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
      if (configRef.current && !configRef.current.contains(e.target)) {
        setShowConfigDropdown(false);
      }
      if (officeSwitcherRef.current && !officeSwitcherRef.current.contains(e.target)) {
        setShowOfficeSwitcher(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close all dropdowns
  const closeAllDropdowns = () => {
    setShowCartDropdown(false);
    setShowNotificationsDropdown(false);
    setShowFavoritesDropdown(false);
    setShowUserMenu(false);
    setShowConfigDropdown(false);
  };

  // Toggle dropdown (close others first)
  const toggleDropdown = (dropdownSetter, currentState) => {
    closeAllDropdowns();
    dropdownSetter(!currentState);
  };

  const handleNavClick = (tab) => {
    const protectedTabs = ['calendar', 'orders', 'profile', 'favorites', 'notifications'];
    if (protectedTabs.includes(tab)) {
      requireAuth(() => setActiveTab(tab));
    } else {
      setActiveTab(tab);
    }
  };

  const handleMenuEnter = (groupKey) => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
    setActiveMenuGroup(groupKey);
  };

  const handleMenuLeave = () => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
    }
    menuTimeoutRef.current = setTimeout(() => {
      setActiveMenuGroup(null);
    }, 300);
  };

  const handleCategoryClick = (category) => {
    setActiveMenuGroup(null);
    onNavigate('search-results', { query: category.name, category: category.slug });
  };

  const handleGroupClick = (groupKey) => {
    setActiveMenuGroup(null);
    onNavigate('search-results', { query: tg(groupKey), group: groupKey });
  };

  // Get cart items with product details
  const cartItems = cart.map(item => {
    const product = PRODUCTS.find(p => p.id === item.id);
    const business = product ? OFFICES.find(b => b.id === product.officeId) : null;
    return { ...item, product, business };
  }).filter(item => item.product);

  const cartTotal = cartItems.reduce((sum, item) => sum + (item.product.price * item.qty), 0);

  // Dropdown Panel Component
  const DropdownPanel = ({ children, className = '' }) => (
    <div className={`absolute top-full right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50 animate-slide-down ${className}`}>
      {children}
    </div>
  );

  // Main menu items (reduced to fit header cleanly on one line)
  const mainMenuGroups = ['bellezza', 'salute', 'sport', 'food', 'travel', 'transport', 'casa'];
  // Groups moved to "Altro" mega-menu
  const moreMenuGroups = ['auto', 'professionisti', 'formazione', 'eventi', 'shopping', 'digitale'];

  return (
    <header className="hidden lg:block bg-white border-b border-gray-200 sticky top-0 z-[100]">
      <div className="max-w-screen-2xl mx-auto">
        {/* Main Header Row */}
        <div className="flex items-center justify-between px-6 py-3">
          {/* Logo */}
          <button 
            onClick={() => isPartnerMode ? setActiveTab('dashboard') : handleNavClick('explore')}
            className="flex-shrink-0"
            data-testid="desktop-logo"
          >
            <img src={LOGOS.full} alt="Skipres" className="h-8" />
          </button>

          {/* Partner Navigation - Shown when in partner mode */}
          {isPartnerMode ? (
            <nav className="flex-1 flex items-center justify-center">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    activeTab === 'dashboard' && !currentPage
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  data-testid="partner-nav-dashboard"
                >
                  Dashboard
                </button>
                <button
                  onClick={() => setActiveTab('calendar')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    activeTab === 'calendar' && !currentPage
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  data-testid="partner-nav-agenda"
                >
                  Agenda
                </button>
                <button
                  onClick={() => setActiveTab('orders')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    activeTab === 'orders' && !currentPage
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  data-testid="partner-nav-ordini"
                >
                  Ordini
                </button>
                
                {/* Configurazione dropdown - Diviso in Company e Office */}
                <div className="relative" ref={configRef}>
                  <button
                    onClick={() => toggleDropdown(setShowConfigDropdown, showConfigDropdown)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                      showConfigDropdown || ['office-catalog', 'office-operators', 'office-profile', 'office-hours', 'company-settings', 'company-offices', 'daily-pricing', 'shipping-config'].includes(currentPage)
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    data-testid="partner-nav-config"
                  >
                    Configurazione
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showConfigDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showConfigDropdown && (
                    <DropdownPanel className="w-80 left-0 right-auto">
                      <div className="py-1">
                        {/* COMPANY Section */}
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Azienda</p>
                        </div>
                        {[
                          { id: 'company-settings', icon: Briefcase, label: 'Info Azienda', desc: 'Nome, logo, descrizione', bgClass: 'bg-slate-100', iconClass: 'text-slate-600' },
                          { id: 'company-offices', icon: Building2, label: 'Gestione Sedi', desc: `${companyOffices?.length || 0} sedi configurate`, bgClass: 'bg-amber-100', iconClass: 'text-amber-600' },
                        ].map((item) => {
                          const Icon = item.icon;
                          const isActive = currentPage === item.id;
                          return (
                            <button
                              key={item.id}
                              onClick={() => { onNavigate(item.id); setShowConfigDropdown(false); }}
                              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left transition-colors ${isActive ? 'bg-emerald-50' : ''}`}
                              data-testid={`partner-config-nav-${item.id}`}
                            >
                              <div className={`w-9 h-9 ${item.bgClass} rounded-lg flex items-center justify-center`}>
                                <Icon className={`w-4.5 h-4.5 ${item.iconClass}`} />
                              </div>
                              <div className="flex-1">
                                <p className={`text-sm font-medium ${isActive ? 'text-emerald-700' : 'text-gray-900'}`}>{item.label}</p>
                                <p className="text-xs text-gray-500">{item.desc}</p>
                              </div>
                              {isActive && <div className="w-2 h-2 bg-emerald-500 rounded-full" />}
                            </button>
                          );
                        })}
                        
                        {/* OFFICE Section */}
                        <div className="px-4 py-2 border-b border-t border-gray-100 mt-2 bg-gray-50/50">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Impostazioni Sede</p>
                            {companyOffices && companyOffices.length > 1 && (
                              <div className="relative">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setShowOfficeSwitcher(!showOfficeSwitcher); }}
                                  className="flex items-center gap-1.5 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium hover:bg-emerald-200 transition-colors"
                                  data-testid="config-office-switcher"
                                >
                                  <MapPin className="w-3 h-3" />
                                  <span className="max-w-[100px] truncate">{selectedOffice?.name?.split(' - ').pop() || 'Sede'}</span>
                                  <ChevronDown className={`w-3 h-3 transition-transform ${showOfficeSwitcher ? 'rotate-180' : ''}`} />
                                </button>
                                {showOfficeSwitcher && (
                                  <div className="absolute top-full right-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-100 z-50 py-1" onClick={(e) => e.stopPropagation()}>
                                    {companyOffices.map(office => (
                                      <button
                                        key={office.id}
                                        onClick={(e) => { e.stopPropagation(); switchOffice(office.id); setShowOfficeSwitcher(false); }}
                                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${office.id === selectedOffice?.id ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-gray-50 text-gray-700'}`}
                                      >
                                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                                        <span className="truncate">{office.name}</span>
                                        {office.id === selectedOffice?.id && <Check className="w-3.5 h-3.5 ml-auto flex-shrink-0" />}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {[
                          { id: 'office-profile', icon: Store, label: 'Vetrina', desc: 'Copertina, info sede', bgClass: 'bg-emerald-100', iconClass: 'text-emerald-600' },
                          { id: 'office-catalog', icon: Package, label: 'Catalogo', desc: 'Servizi, prodotti, eventi', bgClass: 'bg-blue-100', iconClass: 'text-blue-600' },
                          { id: 'office-operators', icon: User, label: 'Team', desc: 'Operatori e personale', bgClass: 'bg-purple-100', iconClass: 'text-purple-600' },
                          { id: 'office-hours', icon: Clock, label: 'Orari', desc: 'Aperture e chiusure', bgClass: 'bg-orange-100', iconClass: 'text-orange-600' },
                          { id: 'daily-pricing', icon: CalendarDays, label: 'Prezzi Giornalieri', desc: 'Noleggi, camere, barche', bgClass: 'bg-cyan-100', iconClass: 'text-cyan-600' },
                          { id: 'shipping-config', icon: Truck, label: 'Spedizioni', desc: 'Zone e costi consegna', bgClass: 'bg-rose-100', iconClass: 'text-rose-600' },
                        ].map((item) => {
                          const Icon = item.icon;
                          const isActive = currentPage === item.id;
                          return (
                            <button
                              key={item.id}
                              onClick={() => { onNavigate(item.id); setShowConfigDropdown(false); }}
                              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left transition-colors ${isActive ? 'bg-emerald-50' : ''}`}
                              data-testid={`partner-config-nav-${item.id}`}
                            >
                              <div className={`w-9 h-9 ${item.bgClass} rounded-lg flex items-center justify-center`}>
                                <Icon className={`w-4.5 h-4.5 ${item.iconClass}`} />
                              </div>
                              <div className="flex-1">
                                <p className={`text-sm font-medium ${isActive ? 'text-emerald-700' : 'text-gray-900'}`}>{item.label}</p>
                                <p className="text-xs text-gray-500">{item.desc}</p>
                              </div>
                              {isActive && <div className="w-2 h-2 bg-emerald-500 rounded-full" />}
                            </button>
                          );
                        })}
                      </div>
                    </DropdownPanel>
                  )}
                </div>
              </div>
            </nav>
          ) : (
          /* Consumer Category Menu - Shown when NOT in partner mode */
          <nav className="flex-1 flex items-center justify-center" ref={menuRef}>
            <div className="flex items-center gap-1">
              {mainMenuGroups.map((groupKey) => {
                const config = CATEGORY_GROUPS_CONFIG[groupKey];
                const categories = groupedCategories[groupKey] || [];
                if (!config || categories.length === 0) return null;

                return (
                  <div
                    key={groupKey}
                    className="relative"
                    onMouseEnter={() => handleMenuEnter(groupKey)}
                    onMouseLeave={handleMenuLeave}
                  >
                    <button
                      onClick={() => handleGroupClick(groupKey)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                        activeMenuGroup === groupKey 
                          ? 'bg-gray-100 text-gray-900' 
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                      data-testid={`menu-${groupKey}`}
                    >
                      {tg(config.nameKey)}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeMenuGroup === groupKey ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Mega Menu Dropdown */}
                    {activeMenuGroup === groupKey && (
                      <div 
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50"
                        onMouseEnter={() => handleMenuEnter(groupKey)}
                        onMouseLeave={handleMenuLeave}
                      >
                        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 min-w-[400px] max-w-[600px] animate-slide-down">
                          {/* Group Header */}
                          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                            <h3 className="font-semibold text-gray-900">{tg(config.nameKey)}</h3>
                            <button 
                              onClick={() => handleGroupClick(groupKey)}
                              className="text-sm text-emerald-600 font-medium hover:text-emerald-700 flex items-center gap-1"
                            >
                              {t('common', 'viewAll')}
                              <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Categories Grid */}
                          <div className="grid grid-cols-2 gap-2">
                            {categories.slice(0, 12).map((cat) => (
                              <button
                                key={cat.id}
                                onClick={() => handleCategoryClick(cat)}
                                className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left group"
                                data-testid={`submenu-${cat.slug}`}
                              >
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${getCategoryBgClass(cat.color)} group-hover:scale-110 transition-transform`}>
                                  <CategoryIcon slug={cat.slug} color={cat.color} size="md" />
                                </div>
                                <span className="text-sm text-gray-700 group-hover:text-gray-900 font-medium">{tc(cat.slug) || cat.name}</span>
                              </button>
                            ))}
                          </div>

                          {/* Show more if needed */}
                          {categories.length > 12 && (
                            <button
                              onClick={() => handleGroupClick(groupKey)}
                              className="mt-3 w-full py-2 text-sm text-gray-500 hover:text-emerald-600 font-medium text-center"
                            >
                              +{categories.length - 12} {t('header', 'moreCategories')}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* More Menu - Now a full mega-menu with all remaining groups */}
              <div
                className="relative"
                onMouseEnter={() => handleMenuEnter('more')}
                onMouseLeave={handleMenuLeave}
              >
                <button
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                    activeMenuGroup === 'more' 
                      ? 'bg-gray-100 text-gray-900' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {t('header', 'other')}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeMenuGroup === 'more' ? 'rotate-180' : ''}`} />
                </button>

                {activeMenuGroup === 'more' && (
                  <div 
                    className="absolute top-full right-0 pt-2 z-50"
                    onMouseEnter={() => handleMenuEnter('more')}
                    onMouseLeave={handleMenuLeave}
                  >
                    <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 min-w-[700px] animate-slide-down">
                      <div className="grid grid-cols-3 gap-6">
                        {moreMenuGroups.map((groupKey) => {
                          const config = CATEGORY_GROUPS_CONFIG[groupKey];
                          const categories = groupedCategories[groupKey] || [];
                          if (!config || categories.length === 0) return null;
                          
                          return (
                            <div key={groupKey} className="min-w-0">
                              <button
                                onClick={() => handleGroupClick(groupKey)}
                                className="flex items-center justify-between w-full mb-2 pb-2 border-b border-gray-100 group"
                              >
                                <span className="font-semibold text-gray-900 text-sm">{tg(config.nameKey)}</span>
                                <ArrowRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-emerald-500 transition-colors" />
                              </button>
                              <div className="space-y-1">
                                {categories.slice(0, 5).map((cat) => (
                                  <button
                                    key={cat.id}
                                    onClick={() => handleCategoryClick(cat)}
                                    className="flex items-center gap-2 py-1.5 text-left hover:text-emerald-600 transition-colors w-full"
                                  >
                                    <div className={`w-6 h-6 rounded flex items-center justify-center ${getCategoryBgClass(cat.color)}`}>
                                      <CategoryIcon slug={cat.slug} color={cat.color} size="sm" />
                                    </div>
                                    <span className="text-sm text-gray-600 truncate">{tc(cat.slug) || cat.name}</span>
                                  </button>
                                ))}
                                {categories.length > 5 && (
                                  <button
                                    onClick={() => handleGroupClick(groupKey)}
                                    className="text-xs text-emerald-600 hover:text-emerald-700 font-medium pt-1"
                                  >
                                    +{categories.length - 5} {t('header', 'more')}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </nav>
          )}

          {/* Divider */}
          <div className="h-8 w-px bg-gray-200 mx-3" />

          {/* Right Actions */}
          <div className="flex items-center gap-1">
            {/* Favorites Dropdown - Consumer only */}
            {!isPartnerMode && (
            <div className="relative" ref={favoritesRef}>
              <button
                onClick={() => {
                  if (isLoggedIn) {
                    toggleDropdown(setShowFavoritesDropdown, showFavoritesDropdown);
                  } else {
                    requireAuth(() => setShowFavoritesDropdown(true));
                  }
                }}
                className="p-2.5 hover:bg-gray-100 rounded-full transition-colors relative group"
                title="Preferiti"
                data-testid="favorites-btn"
              >
                <Heart className="w-5 h-5 text-gray-600 group-hover:text-gray-900" />
              </button>

              {showFavoritesDropdown && (
                <DropdownPanel className="w-80">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900">{t('header', 'yourFavorites')}</h3>
                    <p className="text-xs text-gray-500">{favorites.length} {t('header', 'saved')}</p>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {favorites.length > 0 ? favorites.map(fav => (
                      <button
                        key={fav.id}
                        onClick={() => { onNavigate('office', { id: fav.id }); setShowFavoritesDropdown(false); }}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="relative">
                          <img src={fav.image || fav.images?.[0] || 'https://via.placeholder.com/100'} alt={fav.name} className="w-12 h-12 rounded-xl object-cover" />
                          {fav.isElit && (
                            <div className="absolute -top-1 -left-1 w-5 h-5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center">
                              <Crown className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate text-sm">{fav.name}</h4>
                          <p className="text-xs text-gray-500">{fav.category}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                            <span className="text-xs text-gray-600">{fav.rating}</span>
                          </div>
                        </div>
                        <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                      </button>
                    )) : (
                      <div className="p-6 text-center text-gray-500">
                        <Heart className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                        <p className="text-sm">Nessun preferito salvato</p>
                      </div>
                    )}
                  </div>
                  <div className="p-3 border-t border-gray-100">
                    <button 
                      onClick={() => { onNavigate('favorites'); setShowFavoritesDropdown(false); }}
                      className="w-full py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
                    >
                      {t('header', 'viewAllFavorites')}
                    </button>
                  </div>
                </DropdownPanel>
              )}
            </div>
            )}

            {/* Notifications Dropdown */}
            <div className="relative" ref={notificationsRef}>
              <button
                onClick={() => {
                  if (isLoggedIn) {
                    toggleDropdown(setShowNotificationsDropdown, showNotificationsDropdown);
                  } else {
                    requireAuth(() => setShowNotificationsDropdown(true));
                  }
                }}
                className="p-2.5 hover:bg-gray-100 rounded-full transition-colors relative group"
                title="Notifiche"
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5 text-gray-600 group-hover:text-gray-900" />
                {notifications.filter(n => !n.read).length > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white" />
                )}
              </button>

              {showNotificationsDropdown && (
                <DropdownPanel className="w-96">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{t('nav', 'notifications')}</h3>
                      <p className="text-xs text-gray-500">{notifications.filter(n => !n.read).length} {t('header', 'unread')}</p>
                    </div>
                    <button className="text-xs text-emerald-600 font-medium hover:text-emerald-700">
                      {t('header', 'markAllAsRead')}
                    </button>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length > 0 ? notifications.map(notif => (
                      <div 
                        key={notif.id} 
                        className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-50 ${!notif.read ? 'bg-emerald-50/30' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            notif.type === 'booking_confirmed' || notif.type === 'booking_reminder' || notif.type === 'new_booking' ? 'bg-blue-100 text-blue-600' :
                            notif.type === 'promo' ? 'bg-amber-100 text-amber-600' :
                            notif.type === 'order_shipped' ? 'bg-emerald-100 text-emerald-600' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {notif.type.includes('booking') ? <Calendar className="w-5 h-5" /> : 
                             notif.type === 'promo' ? <Gift className="w-5 h-5" /> : 
                             <Package className="w-5 h-5" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className="font-medium text-gray-900 text-sm">{notif.title}</p>
                              {!notif.read && <div className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0 mt-1" />}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                            <p className="text-xs text-gray-400 mt-1">{notif.time}</p>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="p-6 text-center text-gray-500">
                        <Bell className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                        <p className="text-sm">Nessuna notifica</p>
                      </div>
                    )}
                  </div>
                  <div className="p-3 border-t border-gray-100">
                    <button 
                      onClick={() => { onNavigate('notifications'); setShowNotificationsDropdown(false); }}
                      className="w-full py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
                    >
                      {t('header', 'viewAllNotifications')}
                    </button>
                  </div>
                </DropdownPanel>
              )}
            </div>

            {/* Cart Dropdown - Consumer only */}
            {!isPartnerMode && (
            <div className="relative" ref={cartRef}>
              <button
                onClick={() => toggleDropdown(setShowCartDropdown, showCartDropdown)}
                className="p-2.5 hover:bg-gray-100 rounded-full transition-colors relative group"
                title="Carrello"
                data-testid="cart-btn"
              >
                <ShoppingBag className="w-5 h-5 text-gray-600 group-hover:text-gray-900" />
                {cartCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </button>

              {showCartDropdown && (
                <DropdownPanel className="w-96">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900">{t('header', 'yourCart')}</h3>
                    {cartCount > 0 && <p className="text-xs text-gray-500">{cartCount} {t('header', 'items')}</p>}
                  </div>
                  
                  {cartItems.length > 0 ? (
                    <>
                      <div className="max-h-64 overflow-y-auto p-3 space-y-2">
                        {cartItems.map(item => (
                          <div key={item.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-2.5">
                            <img src={item.product.image} alt={item.product.name} className="w-12 h-12 rounded-lg object-cover" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">{item.product.name}</p>
                              <p className="text-xs text-gray-500">{item.business?.name}</p>
                              <p className="font-bold text-emerald-600 text-sm">€{item.product.price}</p>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <div className="flex items-center gap-1 bg-white rounded-full border border-gray-200">
                                <button 
                                  onClick={() => updateCartQty(item.id, Math.max(0, item.qty - 1))}
                                  className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-gray-100"
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <span className="w-5 text-center text-sm font-medium">{item.qty}</span>
                                <button 
                                  onClick={() => updateCartQty(item.id, item.qty + 1)}
                                  className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-gray-100"
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              </div>
                              <button 
                                onClick={() => removeFromCart(item.id, item.type)}
                                className="text-red-500 hover:text-red-600"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="p-3 border-t border-gray-100 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-gray-600 text-sm">{t('booking', 'subtotal')}</span>
                          <span className="text-lg font-bold text-gray-900">€{cartTotal.toFixed(2)}</span>
                        </div>
                        <div className="space-y-2">
                          <button 
                            onClick={() => { onNavigate('cart'); setShowCartDropdown(false); }}
                            className="w-full py-2 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                          >
                            {t('header', 'viewCart')}
                          </button>
                          <button 
                            onClick={() => { onNavigate('checkout'); setShowCartDropdown(false); }}
                            className="w-full py-2 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
                          >
                            {t('header', 'proceedToCheckout')}
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="p-8 text-center">
                      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <ShoppingBag className="w-7 h-7 text-gray-300" />
                      </div>
                      <h4 className="font-medium text-gray-900 mb-1">{t('header', 'cartEmpty')}</h4>
                      <p className="text-sm text-gray-500 mb-4">{t('header', 'noProductsYet')}</p>
                      <button 
                        onClick={() => { handleNavClick('explore'); setShowCartDropdown(false); }}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors"
                      >
                        {t('header', 'exploreProducts')}
                      </button>
                    </div>
                  )}
                </DropdownPanel>
              )}
            </div>
            )}

            {/* Language Selector */}
            <LanguageSelector />

            {/* Office Switcher - Right side, only for partner with multiple offices */}
            {isPartnerMode && companyOffices.length > 1 && (
              <div className="relative" ref={officeSwitcherRef}>
                <button
                  onClick={() => setShowOfficeSwitcher(!showOfficeSwitcher)}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-all"
                  data-testid="office-switcher-btn"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  <span className="max-w-[160px] truncate">{selectedOffice?.name?.split(' - ').pop() || 'Sede'}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${showOfficeSwitcher ? 'rotate-180' : ''}`} />
                </button>
                {showOfficeSwitcher && (
                  <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 z-50 py-2" data-testid="office-switcher-dropdown">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-50">Sedi di {currentCompany?.name}</div>
                    {companyOffices.map(office => (
                      <button
                        key={office.id}
                        onClick={() => { switchOffice(office.id); setShowOfficeSwitcher(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${office.id === selectedOffice?.id ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}
                        data-testid={`office-switch-${office.id}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${office.id === selectedOffice?.id ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                          <MapPin className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${office.id === selectedOffice?.id ? 'text-emerald-700' : 'text-gray-900'}`}>{office.name}</p>
                          <p className="text-xs text-gray-500 truncate">{office.address}</p>
                        </div>
                        {office.id === selectedOffice?.id && <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* User Menu */}
            {isLoggedIn && user ? (
              <div className="relative ml-1" ref={userMenuRef}>
                <button
                  onClick={() => toggleDropdown(setShowUserMenu, showUserMenu)}
                  className="flex items-center gap-2 pl-3 pr-2 py-1.5 hover:bg-gray-100 rounded-full transition-colors"
                  data-testid="user-menu-btn"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold text-sm">
                    {user.name?.charAt(0) || 'U'}
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>

                {showUserMenu && (
                  <DropdownPanel className="w-64">
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold text-lg">
                          {isPartnerMode ? (currentCompany?.name?.charAt(0) || 'B') : (user.name?.charAt(0) || 'U')}
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-sm">{isPartnerMode ? currentCompany?.name : user.name}</p>
                          <div className="flex items-center gap-1">
                            {isPartnerMode ? (
                              <span className="text-xs text-gray-500">{selectedOffice?.name !== currentCompany?.name ? selectedOffice?.name : currentCompany?.category}</span>
                            ) : (
                              <>
                                {user.isElit && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                                <span className="text-xs text-gray-500">{user.isElit ? t('business', 'elitMember') : t('business', 'starMember')}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      <button
                        onClick={() => { handleNavClick('profile'); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
                      >
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{t('nav', 'myProfile')}</span>
                      </button>
                      {!isPartnerMode && (
                        <>
                          <button
                            onClick={() => { handleNavClick('orders'); setShowUserMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
                          >
                            <Package className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-700">{t('nav', 'myOrders')}</span>
                          </button>
                          <button
                            onClick={() => { handleNavClick('calendar'); setShowUserMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
                          >
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-700">{t('nav', 'calendar')}</span>
                          </button>
                          <button
                            onClick={() => { onNavigate('payments'); setShowUserMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
                          >
                            <CreditCard className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-700">{t('nav', 'paymentMethods')}</span>
                          </button>
                          <button
                            onClick={() => { onNavigate('star'); setShowUserMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
                          >
                            <Star className="w-4 h-4 text-purple-500" />
                            <span className="text-sm text-gray-700">{t('nav', 'starProgram')}</span>
                          </button>
                          <button
                            onClick={() => { onNavigate('elit'); setShowUserMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
                          >
                            <Crown className="w-4 h-4 text-amber-500" />
                            <span className="text-sm text-gray-700">Programma Elit</span>
                          </button>
                        </>
                      )}
                    </div>

                    {/* Logout */}
                    <div className="border-t border-gray-100 p-2">
                      <button
                        onClick={() => { logout(); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-red-50 rounded-xl text-left text-red-600 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm">{t('common', 'logout')}</span>
                      </button>
                    </div>
                  </DropdownPanel>
                )}
              </div>
            ) : (
              <button
                onClick={() => requireAuth(() => {})}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors text-sm font-medium ml-1"
                data-testid="login-btn"
              >
                <User className="w-4 h-4" />
                {t('common', 'login')}
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default DesktopHeader;
